/**
 * SOLID Review
 * S — Acceptable. The game screen's presentation: what its top zone, its overlays and its ad area show, and the screen's reactions —
 *     the table's and the account's watches, entering the game, the ad config, the foreground watchdog, and the reactions of the
 *     four controllers its hooks were (the unload guard, the disconnection overlays, the name modal, the video chat), in the order
 *     the screen declared its effects. Wide, as the screen is; each rule it holds is a few lines.
 * O — Good. A new overlay is one more flag of the snapshot; a new reaction one more entry of the list, in its place.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Acceptable. One snapshot the screen renders and the handful of actions its overlays take.
 * D — Good. The Application's ports, the request contexts, the shared presentation state, the platform's roles and the socket's
 *     live state (a host resource); the controllers it composes are the factory's.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';
import type { IStateLiveKitSession, LiveKitSessionController, IPayloadLiveKitCommit } from './LiveKitSessionController';
import type { IdentityModalController, IStateIdentityModal } from './IdentityModalController';
import type { DisconnectionMonitor, IPortDisconnectionOverlay, TMonitorInputs } from './DisconnectionMonitor';
import type { IPortPageUnloadGuard, IPropsSessionSnapshot, PageUnloadGuard } from './PageUnloadGuard';
import type { ClientRequests } from '../client/ClientRequests';
import type { IStateUi, UiState } from '../stores/UiState';
import type { IServiceWsConnection } from '../uml-components/ComponentFramework';
import type { IServicePlayerDisplayInfo } from '../components/layout/topSection/types';
import { buildTopPlayers, type IServiceTopPlayersInput } from '../components/layout/topSection/buildTopPlayers';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import { GamePhase } from '../../application/ports/ClientVocabulary';

/** The table's watch carries no command: it watches the table as it stands. */
const WATCH: TClientUseCaseInput<'watchTable'> = {};
const UNWATCH: TClientUseCaseInput<'unwatchTable'> = {};
/** The account's watch likewise: the seated player's pushed balance (strict-ports FE4c). */
const WATCH_ACCOUNT: TClientUseCaseInput<'watchAccount'> = {};
const UNWATCH_ACCOUNT: TClientUseCaseInput<'unwatchAccount'> = {};
/** The ad slot's config: the seated free player's, asked for once (strict-ports FE4d). */
const LOAD_AD_CONFIG: TClientUseCaseInput<'loadAdConfig'> = {};
/** Back to the menu from a table the server closed: there is nothing to tell it. */
const LEAVE_CLOSED_TABLE: TClientUseCaseInput<'leaveTable'> = { notifyServer: false };
/** Giving up on a disconnected opponent: the server is told the seat is free. */
const LEAVE_AND_TELL: TClientUseCaseInput<'leaveTable'> = { notifyServer: true };
/** Entering the game screen carries no command: the session knows where the player is headed. */
const ENTER: TClientUseCaseInput<'enterGame'> = {};
/** How long after a return to the foreground the socket has to be back before the page reloads. */
const WATCHDOG_GRACE_MS = 8_000;
/** One top banner's height: the roster and the headphone pill sit below the showing ones. */
const BANNER_HEIGHT = 41;
/** The phases a suspended opponent can block — LOBBY and GAME_OVER own their states. */
const BLOCKING_PHASES: ReadonlySet<GamePhase> = Object.freeze(new Set<GamePhase>([
  GamePhase.ROLLING, GamePhase.PEEKING, GamePhase.CLAIMING, GamePhase.DECIDING, GamePhase.REVEALING,
]));

/** The overlay changes the screen makes — named, as every argument bag to our own functions is (`lint:literals`). */
const RECONNECTING_SHOWN: Partial<IStateAppRootOwn> = { showReconnecting: true };
const RECONNECTING_HIDDEN: Partial<IStateAppRootOwn> = { showReconnecting: false };
const DISCONNECTED_SHOWN: Partial<IStateAppRootOwn> = { showDisconnected: true };
const DISCONNECTED_HIDDEN: Partial<IStateAppRootOwn> = { showDisconnected: false };
const WAITING_DISMISSED: Partial<IStateAppRootOwn> = { waitingOverlayDismissed: true };
const WAITING_SHOWN: Partial<IStateAppRootOwn> = { waitingOverlayDismissed: false };
const NO_OVERLAYS: IStateAppRootOwn = { showReconnecting: false, showDisconnected: false, waitingOverlayDismissed: false };

type TView = TClientViewState;

/** What the screen reads of the client view — the selectors it held one by one: a change of any is a render, of none is not. */
interface IStateAppRootReads {
  readonly localUser: TView['localUser'];
  readonly accountKind: TView['session']['account']['kind'];
  readonly room: TView['room'];
  readonly playerLives: TView['playerLives'];
  readonly activeRollerId: TView['activeRollerId'];
  readonly activeRoundId: TView['activeRoundId'];
  readonly lastClaimedScore: TView['lastClaimedScore'];
  readonly claimWasBlind: TView['claimWasBlind'];
  readonly phase: TView['phase'];
  readonly suspendedPlayerIds: TView['suspendedPlayerIds'];
  readonly decisionCountdown: TView['decisionCountdown'];
  readonly connectionState: TView['connectionState'];
  readonly challengerId: TView['derived']['challengerId'];
  readonly blockingOpponentId: TView['derived']['blockingOpponentId'];
  readonly roomEnded: TView['roomEnded'];
  readonly adZone: TView['session']['adZone'];
  readonly ads: TView['session']['ads'];
  readonly isRestoringSession: TView['isRestoringSession'];
}

/** The screen's own state: the two disconnection overlays (the monitor's port) and the waiting modal's dismissal. */
interface IStateAppRootOwn {
  readonly showReconnecting: boolean;
  readonly showDisconnected: boolean;
  readonly waitingOverlayDismissed: boolean;
}

/** The game screen, as its view renders it. */
export interface IStateAppRoot {
  /** The roster rows above the board, and the two players in a challenge. */
  readonly topPlayers: ReadonlyArray<IServicePlayerDisplayInfo>;
  readonly battlePlayerIds: readonly [string, string] | null;
  /** A player with match credits plays without ads. */
  readonly isPremium: boolean;
  /** The table's video chat, as the screen reads it. */
  readonly media: IStateLiveKitSession;
  /** At a table the video's controls are offered; away from one, none. */
  readonly mediaControls: boolean;
  readonly showEarphoneBanner: boolean;
  readonly earphoneBannerText: string;
  /** Video closed for inactivity: the banner, or — dismissed — its badge. */
  readonly showIdleBanner: boolean;
  readonly showIdleBadge: boolean;
  /** How much of the top the showing banners take. */
  readonly bannersHeight: number;
  readonly showHeadphonePill: boolean;
  readonly headphonePillLabel: string;
  readonly showHeadphonesConnected: boolean;
  /** A suspended opponent the phase waits on: the modal, or — dismissed — its badge. */
  readonly showWaitingOverlay: boolean;
  readonly showWaitingBadge: boolean;
  readonly waitingOpponentName: string;
  readonly serverBusyMessage: string | null;
  readonly isRestoringSession: boolean;
  readonly hasRoom: boolean;
  readonly isIdentityModalOpen: boolean;
  readonly showReconnecting: boolean;
  readonly showDisconnected: boolean;
  readonly showRoomEnded: boolean;
  /** A signed-in registered player who is seated is asked to re-accept changed legal terms. */
  readonly consentGateActive: boolean;
  /** Which ad zone the Domain's rule chose, what the AdSense slot needs, and the Monetag link (asked only for its zone). */
  readonly adZone: TView['session']['adZone'];
  readonly ads: TView['session']['ads'];
  readonly phase: TView['phase'];
  readonly smartLink: string | undefined;
}

/** What a commit hands the screen's reactions: the view's reads and the video chat, as the screen rendered them. */
interface IPayloadAppRootCommit {
  readonly reads: IStateAppRootReads;
  readonly media: IStateLiveKitSession;
}

export interface IConfigDepAppRoot {
  readonly useCases: Pick<IPortClientUseCases,
    'watchTable' | 'unwatchTable' | 'watchAccount' | 'unwatchAccount' | 'loadAdConfig' | 'leaveTable' | 'enterGame'>;
  readonly view: IPortClientStateView;
  readonly requests: ClientRequests;
  readonly ui: UiState;
  readonly platform: Pick<IConfigDepViewPlatform, 'timers' | 'visibility' | 'reload' | 'monetagSmartLink'>;
  /** The socket's state RIGHT NOW — the watchdog's evidence that the incremental recoveries did not take. */
  readonly connection: IServiceWsConnection;
  readonly session: LiveKitSessionController;
  readonly identity: IdentityModalController;
  readonly overlays: (port: IPortDisconnectionOverlay) => DisconnectionMonitor;
  readonly unload: (port: IPortPageUnloadGuard) => PageUnloadGuard;
}

/**
 * THE GAME SCREEN'S MODEL (frontend-framework-oo P3, 2026-10-29, §28.295) — what `components/AppRoot.tsx` kept in its body: the
 * view's sixteen reads, the overlays' state, the derived flags of the banners, badges and overlays, the roster rows, and thirteen
 * effects. The effects are reactions in the order the screen declared them — the hooks' among them, since their controllers are
 * composed here and their reactions run in this one list: the stored-session snapshot the unload guard reads, the guard, the
 * disconnection overlays, the name modal's auto-open, the ad config, entering the game, the video chat's eight, the table's watch
 * and its end, the watchdog, the account's watch and its end, the waiting modal's reset. The binding runs them in React's two
 * phases, so a commit stops and starts them where React stopped and started the screen's effects.
 *
 * The snapshot changes only when one of the reads, the screen's own state, the banner, the name modal or the video chat does — the
 * renders the screen's selectors made, no more.
 */
export class AppRootViewModel implements IServiceViewModel<IStateAppRoot> {
  private readonly state = new ObservableState<IStateAppRootOwn>(NO_OVERLAYS);
  private readonly reactions: Reactions<IPayloadAppRootCommit>;
  /** The game the tab is in as of the last commit — what the unload guard asks before the page goes. */
  private unloadSession: IPropsSessionSnapshot | null = null;
  private reads: IStateAppRootReads | null = null;
  private shown: {
    readonly reads: IStateAppRootReads; readonly own: IStateAppRootOwn; readonly banner: IStateUi; readonly identity: IStateIdentityModal;
    readonly media: IStateLiveKitSession; readonly screen: IStateAppRoot;
  } | null = null;
  /** The facts each snapshot was made from — a commit's reactions read the ones the screen rendered. */
  private readonly factsOf = new WeakMap<IStateAppRoot, IPayloadAppRootCommit>();

  constructor(private readonly deps: IConfigDepAppRoot) {
    const overlays: IPortDisconnectionOverlay = {
      showReconnecting: () => this.state.update(RECONNECTING_SHOWN),
      hideReconnecting: () => this.state.update(RECONNECTING_HIDDEN),
      showDisconnected: () => this.state.update(DISCONNECTED_SHOWN),
      hideDisconnected: () => this.state.update(DISCONNECTED_HIDDEN),
    };
    const unload: IPortPageUnloadGuard = { getSession: () => this.unloadSession };
    const monitor = deps.overlays(overlays);
    const guard = deps.unload(unload);
    const list: Array<IConfigReaction<IPayloadAppRootCommit>> = [
      // The game the guard protects, kept as of the commit (the hook's ref, written by an effect).
      {
        deps: ({ reads }) => [reads.room, reads.localUser],
        run: ({ reads }) => {
          this.unloadSession = reads.room && reads.localUser
            ? { playerId: reads.localUser.playerId, roomId: reads.room.roomId, roomCode: reads.room.roomCode }
            : null;
        },
      },
      ...guard.reactionsOver<IPayloadAppRootCommit>(),
      ...monitor.reactionsOver(({ reads }: IPayloadAppRootCommit): TMonitorInputs => [reads.connectionState, reads.room?.roomId ?? null]),
      ...deps.identity.reactionsOver(({ reads }: IPayloadAppRootCommit) => reads.accountKind === 'anonymous' && !reads.localUser),
      // The ad slot's use case asks for a free, seated player not asked for yet (strict-ports FE4d); this says when to look again.
      { deps: ({ reads }) => [reads.localUser], run: () => { void deps.useCases.loadAdConfig(deps.requests.context(), LOAD_AD_CONFIG); } },
      // Entering the game (strict-ports FE2): one use case, once per mount — a second call (StrictMode) is refused by it.
      { deps: () => [], run: () => { void deps.useCases.enterGame(deps.requests.context(), ENTER); } },
      ...deps.session.reactionsOver(({ reads, media }: IPayloadAppRootCommit): IPayloadLiveKitCommit => ({
        roomId: reads.room?.roomId ?? null, playerId: reads.localUser?.playerId ?? null, rendered: media,
      })),
      // The server's streams for this table (strict-ports FE1e): re-aimed whenever the table's room or round changes; idempotent.
      { deps: ({ reads }) => [reads.room?.roomId, reads.activeRoundId], run: () => { void deps.useCases.watchTable(deps.requests.context(), WATCH); } },
      { deps: () => [], run: () => () => { void deps.useCases.unwatchTable(deps.requests.context(), UNWATCH); } },
      { deps: () => [], run: () => this.watchdog() },
      // The seated player's pushed balance (strict-ports FE4c): re-aimed whenever the seated player changes.
      {
        deps: ({ reads }) => [reads.localUser?.playerId ?? ''],
        run: () => { void deps.useCases.watchAccount(deps.requests.context(), WATCH_ACCOUNT); },
      },
      { deps: () => [], run: () => () => { void deps.useCases.unwatchAccount(deps.requests.context(), UNWATCH_ACCOUNT); } },
      // A resolved or CHANGED blocking suspension resets the dismissal, so the next one surfaces the modal again.
      { deps: ({ reads }) => [reads.blockingOpponentId], run: () => this.state.update(WAITING_SHOWN) },
    ];
    this.reactions = new Reactions<IPayloadAppRootCommit>(list);
  }

  /**
   * The last-resort recovery. After a return to the foreground the incremental recoveries run (§13.30 reconnect, §13.35 re-sync); if
   * the socket is still not connected a few seconds later while the tab is in a game, the OS froze or killed the tab hard, and a
   * full reload is the reliable cure — safe since identity is durable (§13.34/§13.36) and §13.33 resumes the game. A grace timer
   * already running when the screen goes is left to run, as it was.
   */
  private watchdog(): () => void {
    const { visibility, timers, reload } = this.deps.platform;
    return visibility.onReturn(() => {
      if (!visibility.visible()) return;
      timers.setTimeout(() => {
        if (!visibility.visible()) return;
        // A fresh read of the view, and the socket's live state.
        const inGame = !!this.deps.view.getSnapshot().room;
        if (inGame && this.deps.connection.getState() !== 'connected') reload();
      }, WATCHDOG_GRACE_MS);
    });
  }

  // ── the actions ─────────────────────────────────────────────────────────────────────────────────────────────────────

  /** Bail out of a game that waits on a disconnected opponent: the server is told, and the player is back on the front screen. */
  readonly leaveWaiting = (): void => {
    void this.deps.useCases.leaveTable(this.deps.requests.context(), LEAVE_AND_TELL);
  };

  /** The abandoned table's "Back to menu": the stored game and the table go, the identity stays. */
  readonly backToMenuFromRoomEnded = async (): Promise<void> => {
    await this.deps.useCases.leaveTable(this.deps.requests.context(), LEAVE_CLOSED_TABLE);
  };

  /** The waiting modal's ✕ collapses it into a badge; the badge brings it back. */
  readonly dismissWaiting = (): void => this.state.update(WAITING_DISMISSED);
  readonly showWaiting = (): void => this.state.update(WAITING_SHOWN);

  /** The disconnected overlay's own "Reconnect" closes it — the socket reconnects by itself. */
  readonly closeDisconnected = (): void => this.state.update(DISCONNECTED_HIDDEN);

  readonly closeIdentityModal = (): void => this.deps.identity.close();

  // ── the snapshot ────────────────────────────────────────────────────────────────────────────────────────────────────

  /** The view's reads — the same object while every one of them is. */
  private readsOf(view: TView): IStateAppRootReads {
    const next: IStateAppRootReads = {
      localUser: view.localUser,
      accountKind: view.session.account.kind,
      room: view.room,
      playerLives: view.playerLives,
      activeRollerId: view.activeRollerId,
      activeRoundId: view.activeRoundId,
      lastClaimedScore: view.lastClaimedScore,
      claimWasBlind: view.claimWasBlind,
      phase: view.phase,
      suspendedPlayerIds: view.suspendedPlayerIds,
      decisionCountdown: view.decisionCountdown,
      connectionState: view.connectionState,
      challengerId: view.derived.challengerId,
      blockingOpponentId: view.derived.blockingOpponentId,
      roomEnded: view.roomEnded,
      adZone: view.session.adZone,
      ads: view.session.ads,
      isRestoringSession: view.isRestoringSession,
    };
    const before = this.reads;
    const keys = Object.keys(next) as Array<keyof IStateAppRootReads>;
    if (before !== null && keys.every((key) => Object.is(before[key], next[key]))) return before;
    this.reads = next;
    return next;
  }

  getSnapshot(): IStateAppRoot {
    const reads = this.readsOf(this.deps.view.getSnapshot());
    const own = this.state.get();
    const banner = this.deps.ui.getSnapshot();
    const identity = this.deps.identity.getSnapshot();
    const media = this.deps.session.getSnapshot();
    const shown = this.shown;
    if (shown !== null && shown.reads === reads && shown.own === own && shown.banner === banner && shown.identity === identity
      && shown.media === media) return shown.screen;
    const screen = this.screenOf(reads, own, banner, identity, media);
    const facts: IPayloadAppRootCommit = { reads, media };
    this.factsOf.set(screen, facts);
    this.shown = { reads, own, banner, identity, media, screen };
    return screen;
  }

  private screenOf(
    reads: IStateAppRootReads, own: IStateAppRootOwn, banner: IStateUi, identity: IStateIdentityModal, media: IStateLiveKitSession,
  ): IStateAppRoot {
    const atTable = !!reads.room;
    const route = media.audioRoute;
    const muted = route.remoteAudioMuted;
    const showEarphoneBanner = media.showEarphoneHint && !media.earphoneHintDismissed && atTable;
    // Video closed after a while of no play, to save connection time: a way back in, as a banner or (dismissed) a badge.
    const showIdleBanner = media.lkAutoDisconnected && !media.lkIdleBannerDismissed && atTable;
    const showIdleBadge = media.lkAutoDisconnected && media.lkIdleBannerDismissed && atTable;
    const roomPlayers = reads.room?.players ?? [];
    // Only the player the current phase waits on blocks — a passive player going away shows as SUSPENDED and play goes on.
    const suspendedOpponent = reads.blockingOpponentId ? roomPlayers.find((p) => p.id === reads.blockingOpponentId) : undefined;
    const showWaitingForOpponent = atTable && !!suspendedOpponent && BLOCKING_PHASES.has(reads.phase)
      && !reads.roomEnded && !own.showDisconnected && !own.showReconnecting;
    const pillStatus = route.route === 'headphones'
      ? 'Headphones active'
      : route.route === 'speakers' ? 'No headphones detected — echo risk' : 'Headphone status unknown';
    const topPlayersInput: IServiceTopPlayersInput = {
      roomPlayers,
      localUser: reads.localUser,
      playerLives: reads.playerLives,
      suspendedPlayerIds: reads.suspendedPlayerIds,
      activeRollerId: reads.activeRollerId,
      challengerId: reads.challengerId,
      phase: reads.phase,
      lastClaimedScore: reads.lastClaimedScore,
      claimWasBlind: reads.claimWasBlind,
      decisionCountdown: reads.decisionCountdown,
      participants: media.lkParticipants,
      onPressLocalPlaceholder: this.deps.identity.open,
    };
    return {
      topPlayers: buildTopPlayers(topPlayersInput),
      battlePlayerIds: reads.activeRollerId && reads.challengerId ? [reads.activeRollerId, reads.challengerId] : null,
      isPremium: !!reads.localUser && reads.localUser.matchCredits > 0,
      media,
      mediaControls: !!reads.room?.roomId,
      showEarphoneBanner,
      earphoneBannerText: route.route === 'unknown'
        ? "Can't detect headphones on this device — wear headphones to prevent echo."
        : muted ? 'Headphones disconnected — incoming audio muted to prevent echo.' : 'Wear headphones or earphones to prevent echo from speakers.',
      showIdleBanner,
      showIdleBadge,
      bannersHeight: (showEarphoneBanner ? BANNER_HEIGHT : 0) + (showIdleBanner ? BANNER_HEIGHT : 0),
      showHeadphonePill: media.lkConnectionState === 'connected' && atTable,
      headphonePillLabel: `${pillStatus} — incoming audio ${muted ? 'muted' : 'on'}. Tap to ${muted ? 'unmute' : 'mute'}.`,
      showHeadphonesConnected: route.notice === 'connected',
      showWaitingOverlay: showWaitingForOpponent && !own.waitingOverlayDismissed,
      showWaitingBadge: showWaitingForOpponent && own.waitingOverlayDismissed,
      // One name for the modal and its badge: the badge shows only with an opponent (strict-ports FE9).
      waitingOpponentName: suspendedOpponent?.displayName ?? 'your opponent',
      serverBusyMessage: banner.connectionBanner,
      isRestoringSession: reads.isRestoringSession,
      hasRoom: atTable,
      isIdentityModalOpen: identity.isOpen,
      showReconnecting: own.showReconnecting,
      showDisconnected: own.showDisconnected,
      showRoomEnded: reads.roomEnded,
      consentGateActive: reads.accountKind === 'registered' && !!reads.localUser,
      adZone: reads.adZone,
      ads: reads.ads,
      phase: reads.phase,
      smartLink: reads.adZone === 'propeller' ? this.deps.platform.monetagSmartLink() : undefined,
    };
  }

  subscribe(listener: () => void): () => void {
    const leaves = [
      this.deps.view.subscribe(listener),
      this.state.subscribe(listener),
      this.deps.ui.subscribe(listener),
      this.deps.identity.subscribe(listener),
      this.deps.session.subscribe(listener),
    ];
    return () => leaves.forEach((leave) => leave());
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStateAppRoot): void {
    this.reactions.stopChanged(this.factsOf.get(rendered)!);
  }

  commit(_inputs: readonly unknown[], rendered: IStateAppRoot): void {
    this.reactions.startChanged(this.factsOf.get(rendered)!);
  }
}
